import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import type { Check, CheckForm } from '~/types/checks'
import { AdminChecksCreateModal } from '#components'
import POSCheckDetailsDialog from '~/layers/checks/components/POS/CheckDetailsDialog.vue'
import POSCheckReceiptDialog from '~/layers/checks/components/POS/CheckReceiptDialog.vue'
import AdminDeleteWithReasonDialog from '~/components/Admin/DeleteWithReasonDialog.vue'
import { useDateRange } from '~/composables/dates/useDateRange'
import {
  type CheckStatus,
  type DateField,
  normalizeDateRange,
  todayRange,
  validateDateField,
  checkMatchesPaymentMethod,
  validatePaymentMethodId,
} from '~/stores/admin/checks.filters'

export const useChecksStore = defineStore('checks', () => {
  const log = useLogger('store:checks')
  const { get, post, put } = useAuthenticatedFetch()
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)

  const nuxtApp = useNuxtApp()
  const { t } = nuxtApp.$i18n
  const { $dialog } = nuxtApp

  // State
  const checks: Ref<Check[]> = ref([])
  const loading: Ref<boolean> = ref(false)

  // Filter states
  const searchTerm: Ref<string> = ref('')
  const statusFilter: Ref<CheckStatus> = ref('all')
  const dateRange: Ref<[Date, Date]> = ref(todayRange())
  const dateFieldFilter: Ref<DateField> = ref('all')
  const paymentMethodFilter: Ref<number | 'all'> = ref('all')

  // Column visibility (persisted; defaults merged in by the page via ensureColumns)
  const columnVisibility: Ref<Record<string, boolean>> = ref({})

  // Realtime
  const supabase = useSupabaseClient()
  const realtimeChannel = ref()

  // Core data fetching
  async function fetchChecks(dateFilterParam?: Date | Date[], dateFieldParam?: string): Promise<void> {
    if (!businessId.value)
      return

    const activeDateFilter = dateFilterParam ?? dateRange.value
    const activeDateField = dateFieldParam ?? dateFieldFilter.value

    loading.value = true
    try {
      let queryParam = statusFilter.value === 'all' ? '?all=true' : `?status=${statusFilter.value}`

      log.info('fetchChecks', {
        statusFilter: statusFilter.value,
        activeDateFilter,
        activeDateField,
        isArray: Array.isArray(activeDateFilter),
        hasDateFilter: !!activeDateFilter,
      })

      // Add date range if provided
      if (activeDateFilter) {
        const dates = useDateRange(activeDateFilter)

        log.info('useDateRange-result', {
          inputDate: activeDateFilter,
          activeDateField,
          outputStart: dates.start?.toISOString(),
          outputEnd: dates.end?.toISOString(),
          userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })

        if (dates.start && dates.end) {
          const separator = queryParam.includes('?') ? '&' : '?'
          queryParam += `${separator}start=${dates.start.toISOString()}&end=${dates.end.toISOString()}&dateField=${activeDateField}`
        }
      }

      log.info('api-call', {
        url: `/api/business/${businessId.value}/checks${queryParam}`,
        businessId: businessId.value,
        queryParam,
      })

      const res = await get(`/api/business/${businessId.value}/checks${queryParam}`)

      log.info('api-response', {
        success: res.success,
        dataCount: res.data?.length || 0,
        firstCheck: res.data?.[0]?.check_number,
        lastCheck: res.data?.[res.data.length - 1]?.check_number,
      })

      checks.value = res.data || []
    }
    catch (error: any) {
      log.error('Error fetching checks:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('App is offline - using cached checks')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to load checks')
      }
      else {
        useErrorToast('Failed to load checks')
      }
    }
    finally {
      loading.value = false
    }
  }

  async function fetchChecksByShift(shiftId: number): Promise<Check[]> {
    if (!businessId.value) return []
    try {
      const res = await get(`/api/business/${businessId.value}/checks?shift_id=${shiftId}`)
      return res.data || []
    }
    catch {
      useErrorToast('Failed to load shift checks')
      return []
    }
  }

  async function fetchCheckById(checkId: number): Promise<Check | null> {
    if (!businessId.value)
      return null

    try {
      const res = await get(`/api/business/${businessId.value}/checks/${checkId}`)
      return res.data || null
    }
    catch (error: any) {
      log.error('Error fetching check:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('App is offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in')
      }
      else {
        useErrorToast('Failed to load check')
      }
      return null
    }
  }

  // CRUD operations
  async function createCheck(formData: CheckForm): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await post(`/api/business/${businessId.value}/checks`, formData)

      if (res.success) {
        // Toast removed - used by POS internally, no need for user notification
        // useSuccessToast('Check created successfully')
        await fetchChecks()
        return true
      }
      else {
        const errorMsg = res.message ? `Failed to create check: ${res.message}` : 'Failed to create check'
        useErrorToast(errorMsg)
        return false
      }
    }
    catch (error: any) {
      log.error('Error creating check:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot create check while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to create checks')
      }
      else {
        const errorMessage = error?.data?.message || error?.message || 'Unknown error'
        // El servidor manda este mensaje exacto tanto desde la comprobación previa
        // como desde la violación del índice único, así que el cajero ve lo mismo
        // en los dos casos en vez de un error de base de datos.
        if (errorMessage.includes('Table already has an open check')) {
          useErrorToast('Table already has an open check')
        }
        else {
          useErrorToast(`Failed to create check: ${errorMessage}`)
        }
      }
      return false
    }
  }

  async function updateCheck(checkId: number, formData: Partial<CheckForm>): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await put(`/api/business/${businessId.value}/checks/${checkId}`, formData)

      if (res.success) {
        useSuccessToast('Check updated successfully')
        await fetchChecks()
        return true
      }
      else {
        const errorMsg = res.message ? `Failed to update check: ${res.message}` : 'Failed to update check'
        useErrorToast(errorMsg)
        return false
      }
    }
    catch (error: any) {
      log.error('Error updating check:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot update check while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to update checks')
      }
      else {
        const errorMessage = error?.data?.message || error?.message || 'Unknown error'
        useErrorToast(`Failed to update check: ${errorMessage}`)
      }
      return false
    }
  }

  async function updateStatus(checkId: number, status: 'open' | 'closed' | 'cancelled'): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await put(`/api/business/${businessId.value}/checks/${checkId}/status`, { status })

      if (res.success) {
        await fetchChecks()
        return true
      }
      else {
        const errorMsg = res.message ? `Failed to update check status: ${res.message}` : 'Failed to update check status'
        useErrorToast(errorMsg)
        return false
      }
    }
    catch (error: any) {
      log.error('Error updating check status:', error)

      if (error.name === 'OfflineError') {
        useErrorToast('Cannot update check status while offline')
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast('Please sign in to update checks')
      }
      else {
        const errorMessage = error?.data?.message || error?.message || 'Unknown error'
        useErrorToast(`Failed to update check status: ${errorMessage}`)
      }
      return false
    }
  }

  async function deleteCheck(checkId: number, reason?: string): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await post(`/api/business/${businessId.value}/checks/${checkId}/delete`, {
        delete_reason: reason,
      })

      if (res.success) {
        useSuccessToast(t('checks.deleteSuccess'))
        checks.value = checks.value.filter(c => c.id !== checkId)
        fetchChecks()
        return true
      }
      else {
        useErrorToast(t('checks.deleteError'))
        return false
      }
    }
    catch (error: any) {
      log.error('Error deleting check:', error)

      if (error.name === 'OfflineError') {
        useErrorToast(t('checks.deleteOfflineError'))
      }
      else if (error.name === 'AuthenticationRequired') {
        useErrorToast(t('checks.deleteAuthError'))
      }
      else {
        useErrorToast(t('checks.deleteError'))
      }
      return false
    }
  }

  // Dialog and confirmation methods
  async function openCreateModal(): Promise<void> {
    $dialog.open(AdminChecksCreateModal, {
      data: {
        businessId: businessId.value,
      },
      props: {
        header: t('Add new check'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        await fetchChecks()
      },
    })
  }

  async function openEditModal(check: Check): Promise<void> {
    // Fetch fresh check data to ensure we have the latest
    const checkData = await fetchCheckById(check.id)

    if (!checkData) {
      useErrorToast('Failed to load check details')
      return
    }

    $dialog.open(POSCheckDetailsDialog, {
      data: {
        checkData,
      },
      props: {
        header: t('Check Details'),
        ...useDynamicDialogSettings,
      },
      onClose: async () => {
        await fetchChecks()
      },
    })
  }

  async function openReceiptModal(check: Check): Promise<void> {
    const checkData = await fetchCheckById(check.id)
    if (!checkData) {
      useErrorToast('Failed to load check details')
      return
    }
    $dialog.open(POSCheckReceiptDialog, {
      data: { checkData },
      props: {
        header: t('Receipt'),
        ...useDynamicDialogSettings,
      },
    })
  }

  async function deleteCheckWithConfirm(checkId: number): Promise<void> {
    $dialog.open(AdminDeleteWithReasonDialog, {
      data: {
        message: t('Do you want to delete this check? You can add a reason for the audit log.'),
        onConfirm: async (reason?: string) => {
          await deleteCheck(checkId, reason)
        },
      },
      props: {
        header: t('Delete Check'),
        style: { width: '400px' },
        modal: true,
      },
    })
  }

  // Computed properties for filtering
  const filteredChecks = computed(() => {
    let filtered = checks.value

    // Filter by search term
    if (searchTerm.value.trim()) {
      const term = searchTerm.value.toLowerCase()
      filtered = filtered.filter(check =>
        check.check_number.toLowerCase().includes(term)
        || check.customer_name?.toLowerCase().includes(term)
        || check.customer_phone?.toLowerCase().includes(term)
        || check.custom_label?.toLowerCase().includes(term),
      )
    }

    return filtered
  })

  const totalChecks = computed(() => checks.value.length)

  const openChecks = computed(() =>
    checks.value.filter(check => check.status === 'open').length,
  )

  const closedChecks = computed(() =>
    checks.value.filter(check => check.status === 'closed').length,
  )

  const displayedChecks = computed(() =>
    checks.value.filter(check => checkMatchesPaymentMethod(check, paymentMethodFilter.value)))

  // Utility functions
  function getCheckById(id: number): Check | undefined {
    return checks.value.find(check => check.id === id)
  }

  function getCheckByNumber(checkNumber: string): Check | undefined {
    return checks.value.find(check => check.check_number === checkNumber)
  }

  // Search and filter methods
  function setSearchTerm(term: string): void {
    searchTerm.value = term
  }

  function setStatusFilter(status: string): void {
    statusFilter.value = status
    // Note: fetchChecks() is called by the page component watcher to preserve date filter
  }

  function setDateFilter(date: Date | Date[] | null): void {
    dateRange.value = normalizeDateRange(date)
  }

  function setDateFieldFilter(field: string): void {
    dateFieldFilter.value = validateDateField(field)
  }

  function setPaymentMethodFilter(id: number | 'all'): void {
    paymentMethodFilter.value = validatePaymentMethodId(id)
  }

  function ensureColumns(defaults: Record<string, boolean>): void {
    columnVisibility.value = { ...defaults, ...columnVisibility.value }
  }

  function setColumnVisibility(key: string, visible: boolean): void {
    columnVisibility.value = { ...columnVisibility.value, [key]: visible }
  }

  function toggleColumn(key: string): void {
    setColumnVisibility(key, !(columnVisibility.value[key] ?? true))
  }

  function clearFilters(): void {
    searchTerm.value = ''
    statusFilter.value = 'open'
    dateRange.value = todayRange()
    dateFieldFilter.value = 'all'
    paymentMethodFilter.value = 'all'
  }

  // Realtime sync for kitchen status updates
  function setupRealtime(): void {
    if (!businessId.value) {
      log.warn('realtime:setup', 'Cannot setup realtime: businessId not available')
      return
    }

    log.info('realtime:setup', 'Setting up checks realtime sync for business:', businessId.value)

    realtimeChannel.value = supabase
      .channel('pos-checks-sync')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'checks',
          filter: `business_id=eq.${businessId.value}`,
        },
        handleCheckUpdate,
      )
      .subscribe((status, error) => {
        log.info('realtime:status', status)
        if (error) {
          log.error('realtime:error', error)
        }
        if (status === 'SUBSCRIBED') {
          log.info('realtime:subscribed', 'Successfully subscribed to checks updates')
        }
        if (status === 'CHANNEL_ERROR') {
          log.debug('realtime:channel-error', 'Realtime not enabled for admin interface - manual refresh required')
          cleanupRealtime()
        }
        if (status === 'TIMED_OUT') {
          log.error('realtime:timeout', 'Subscription timed out')
        }
        if (status === 'CLOSED') {
          log.warn('realtime:closed', 'Channel closed')
        }
      })
  }

  function handleCheckUpdate(payload: any): void {
    const updatedCheck = payload.new
    const posCheckStore = usePosCheckStore()

    log.info('realtime:event', 'Received UPDATE event for check:', {
      checkId: updatedCheck.id,
      activeCheckId: posCheckStore.activeCheckId,
      status: updatedCheck.status,
      cartItems: updatedCheck.cart?.length || 0,
    })

    // Remove soft-deleted checks immediately
    if (updatedCheck.deleted_at) {
      const checkIndex = checks.value.findIndex(c => c.id === updatedCheck.id)
      if (checkIndex !== -1) checks.value.splice(checkIndex, 1)
      log.info('realtime:removed', `Removed soft-deleted check #${updatedCheck.id}`)
      return
    }

    // If viewing only open checks, remove closed/cancelled from array
    if (statusFilter.value === 'open' && (updatedCheck.status === 'closed' || updatedCheck.status === 'cancelled')) {
      const checkIndex = checks.value.findIndex(c => c.id === updatedCheck.id)
      if (checkIndex !== -1) {
        checks.value.splice(checkIndex, 1)
        log.info('realtime:removed', `Removed non-open check #${updatedCheck.id} (${updatedCheck.status})`)
      }
      return
    }

    // Skip if THIS check is being actively edited
    if (posCheckStore.activeCheckId === updatedCheck.id) {
      log.info('realtime:skip', `Check #${updatedCheck.id} is active, skipping background sync`)
      return
    }

    // Safe to update - check is not active
    const checkIndex = checks.value.findIndex(c => c.id === updatedCheck.id)

    if (checkIndex !== -1) {
      const oldCartLength = checks.value[checkIndex].cart?.length || 0
      const newCartLength = updatedCheck.cart?.length || 0

      checks.value.splice(checkIndex, 1, updatedCheck)

      log.info('realtime:sync', `✅ Updated check #${updatedCheck.id} in background`, {
        checkIndex,
        oldCartItems: oldCartLength,
        newCartItems: newCartLength,
      })
    }
    else {
      log.warn('realtime:not-found', `Check #${updatedCheck.id} not found in local state`, {
        totalChecks: checks.value.length,
        checkIds: checks.value.map(c => c.id),
      })
    }
  }

  function cleanupRealtime(): void {
    if (realtimeChannel.value) {
      supabase.removeChannel(realtimeChannel.value)
      realtimeChannel.value = null
      log.info('realtime:cleanup', 'Realtime channel cleaned up')
    }
  }

  // Watch businessId to setup/cleanup realtime
  watch(businessId, (newId, oldId) => {
    if (oldId)
      cleanupRealtime()
    if (newId)
      setupRealtime()
  }, { immediate: true })

  // Reset method to clear all state
  function reset(): void {
    cleanupRealtime()
    checks.value = []
    loading.value = false
    searchTerm.value = ''
    statusFilter.value = 'open'
    dateRange.value = todayRange()
    dateFieldFilter.value = 'all'
    paymentMethodFilter.value = 'all'
  }

  return {
    // State
    checks,
    loading,
    searchTerm,
    statusFilter,
    dateRange,
    dateFieldFilter,
    paymentMethodFilter,
    columnVisibility,

    // Computed
    filteredChecks,
    totalChecks,
    openChecks,
    closedChecks,
    displayedChecks,

    // Core methods
    fetchChecks,
    fetchChecksByShift,
    fetchCheckById,
    createCheck,
    updateCheck,
    updateStatus,
    deleteCheck,

    // Dialog methods
    openCreateModal,
    openEditModal,
    openReceiptModal,
    deleteCheckWithConfirm,

    // Utility methods
    getCheckById,
    getCheckByNumber,

    // Filter methods
    setSearchTerm,
    setStatusFilter,
    setDateFilter,
    setDateFieldFilter,
    setPaymentMethodFilter,
    ensureColumns,
    setColumnVisibility,
    toggleColumn,
    clearFilters,

    // Reset method
    reset,
  }
}, {
  persist: {
    key: 'checks',
    storage: localStorage,
    // dateRange is intentionally NOT persisted — it lives in the URL (shareable).
    // statusFilter is NOT persisted either: it must start as 'all' on every load,
    // applying only when the user explicitly changes it in-session.
    paths: ['checks', 'dateFieldFilter', 'columnVisibility'],
    afterRestore: (ctx) => {
      // Sanitize restored filters so a corrupt localStorage value can never poison state.
      ctx.store.dateFieldFilter = validateDateField(ctx.store.dateFieldFilter)
      if (typeof ctx.store.columnVisibility !== 'object' || ctx.store.columnVisibility === null) {
        ctx.store.columnVisibility = {}
      }
    },
  },
})

export const createChecksStore = async (businessIdParam: string) => {
  const checksStore = useChecksStore()
  const businessStore = useBusinessStore()

  businessStore.setBusinessId(businessIdParam)
  await checksStore.fetchChecks()

  return checksStore
}
